import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { createEmailPasswordSession, account } from '../appwrite/appwrite';
import { resolveLoginIdentifier, getLoginHints } from '../lib/auth';
import { describeRecoveryError } from '../lib/account';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion } from 'framer-motion';

/**
 * Prüft, ob ein Appwrite-Fehler darauf hindeutet, dass für das Konto bereits
 * eine Session existiert. In dem Fall ist die Anmeldung faktisch erfolgreich.
 */
const isAlreadyAuthenticated = (error) => {
  const message = String(error?.message || '');
  const type = String(error?.type || '');
  return (
    /already authorized/i.test(message) ||
    /already active/i.test(message) ||
    /session .*already/i.test(message) ||
    /already_authenticated/i.test(type)
  );
};

/**
 * Übersetzt eine Appwrite-Fehlermeldung in eine deutsche, nutzerfreundliche
 * Meldung. Der originale Stacktrace wird bewusst nicht angezeigt.
 */
const toGermanError = (error) => {
  const message = String(error?.message || '');

  if (/invalid credentials/i.test(message)) {
    return 'Benutzername oder Passwort ist falsch.';
  }
  if (/user .*not found|user_not_found/i.test(message)) {
    return 'Kein Konto mit dieser E-Mail-Adresse gefunden.';
  }
  if (/password must be/i.test(message)) {
    return 'Das Passwort erfüllt die Anforderungen der Plattform nicht.';
  }
  if (/too many requests|429/i.test(message)) {
    return 'Zu viele Anmeldeversuche. Bitte kurz warten und es erneut versuchen.';
  }
  if (/failed to fetch|network|load failed/i.test(message)) {
    return 'Verbindung zum Server fehlgeschlagen. Bitte Internetverbindung prüfen.';
  }
  if (/server error|internal|500/i.test(message)) {
    return 'Der Server meldet einen Fehler. Bitte später erneut versuchen.';
  }

  return 'Anmeldung fehlgeschlagen. Bitte E-Mail-Adresse und Passwort prüfen.';
};

const Login = () => {
  const navigate = useNavigate();
  const { login } = useApp();
  const [formData, setFormData] = useState({ identifier: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryTarget, setRecoveryTarget] = useState('');
  const [recoverySent, setRecoverySent] = useState(false);
  const [recoveryError, setRecoveryError] = useState('');
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const hints = useMemo(() => getLoginHints(), []);

  /**
   * Passwort-Zurücksetzen anfordern: Appwrite schickt einen Link mit
   * userId + secret an die hinterlegte Adresse. Der Link fuehrt direkt auf
   * die Seite /reset-password.
   *
   * Akzeptiert wird der Benutzername (z. B. "Salih85") genauso wie die
   * E-Mail-Adresse - der Benutzername wird ueber die Team-Liste aufgelöst.
   */
  const handleRecovery = async (e) => {
    e.preventDefault();
    if (recoveryLoading) return;

    setRecoveryError('');
    const input = recoveryEmail.trim();
    if (!input) {
      setRecoveryError('Bitte eine E-Mail-Adresse oder den Benutzernamen eingeben.');
      return;
    }

    // Benutzername -> Konto-E-Mail (leer, wenn unbekannt)
    const { email } = resolveLoginIdentifier(input);
    if (!email) {
      setRecoveryError(
        'Diese Eingabe gehört zu keinem Konto. Bitte die E-Mail-Adresse oder den Benutzernamen prüfen.'
      );
      return;
    }

    setRecoveryLoading(true);
    try {
      const url = `${window.location.origin}/#/reset-password`;
      await account.createRecovery(email, url);
      setRecoveryTarget(email);
      setRecoverySent(true);
    } catch (err) {
      console.error('Recovery konnte nicht gestartet werden:', err?.message);
      setRecoveryError(describeRecoveryError(err));
    } finally {
      setRecoveryLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setError('');
    setLoading(true);

    const identifier = formData.identifier.trim();
    // Leerzeichen am Passwort entfernen: beim Kopieren aus Chat oder
    // Passwort-Manager landen haeufig unsichtbare Leerzeichen/Newlines
    // am Ende, was sonst als "Invalid credentials" endet.
    const password = formData.password.trim();

    try {
      // Benutzername (z. B. "Dervis90") oder E-Mail-Adresse -> Konto-E-Mail
      const { email, name, known } = resolveLoginIdentifier(identifier);
      if (!known || !email) {
        setError('Unbekannter Benutzername. Bitte Benutzername oder E-Mail-Adresse prüfen.');
        setFormData((prev) => ({ ...prev, password: '' }));
        setLoading(false);
        return;
      }

      // Bevorzugter Pfad: die zentrale, asynchrone Login-Funktion aus dem AppContext.
      if (typeof login === 'function') {
        const result = await login(email, password);

        if (result === true || (result && result !== false && result.success !== false)) {
          navigate('/');
          return;
        }

        // login hat kein eindeutiges Ergebnis geliefert -> Fehler
        setError(`Anmeldung für "${name}" (${email}) fehlgeschlagen. Bitte das Passwort prüfen.`);
        setFormData((prev) => ({ ...prev, password: '' }));
        return;
      }

      // Rückfallpfad, falls der Context (noch) keine Login-Funktion liefert:
      // direkte Appwrite-Session über den Account-Client.
      await createEmailPasswordSession(email, password);
      navigate('/');
    } catch (err) {
      if (isAlreadyAuthenticated(err)) {
        // Session existiert bereits -> als Erfolg behandeln.
        navigate('/');
        return;
      }

      setError(toGermanError(err));
      setFormData((prev) => ({ ...prev, password: '' }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-8 text-center">
            <div className="w-40 h-40 flex items-center justify-center mx-auto mb-4">
              <img 
                src="https://media-manager-c.questera.ai/greta-media/b64cc446cdd47675bdad19de596fda49c979fcc46f742bafa38ab384e40234c3cc3101a26c56d32c5dccb06920b38db8/images/aW1hZ2UvcG5n/032e1220d38820869e7fe9bbbe32aea8.png" 
                alt="ChargeNest Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            <p className="text-indigo-100 text-sm">Willkommen zurück</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8 space-y-5">
            {error && (
              <motion.div 
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2"
              >
                <SafeIcon icon={FiIcons.FiAlertCircle} />
                <span className="text-sm">{error}</span>
              </motion.div>
            )}

            <div>
              <label htmlFor="login-identifier" className="block text-sm font-medium text-gray-700 mb-2">
                Benutzername oder E-Mail
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SafeIcon icon={FiIcons.FiUser} className="text-gray-400" />
                </div>
                <input
                  id="login-identifier"
                  name="username"
                  type="text"
                  required
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  value={formData.identifier}
                  onChange={(e) => setFormData({ ...formData, identifier: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                  placeholder="z. B. Dervis90 oder E-Mail-Adresse"
                />
              </div>
              {hints.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {hints.map((h) => (
                    <button
                      key={h.id}
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, identifier: h.name }))}
                      className="text-xs px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors"
                      title={h.email}
                    >
                      {h.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label htmlFor="login-password" className="block text-sm font-medium text-gray-700 mb-2">
                Passwort
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SafeIcon icon={FiIcons.FiLock} className="text-gray-400" />
                </div>
                <input
                  id="login-password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full pl-10 pr-12 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                  placeholder="Passwort eingeben"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <SafeIcon icon={showPassword ? FiIcons.FiEyeOff : FiIcons.FiEye} />
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold py-3 rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? 'Anmelden…' : 'Anmelden'}
            </button>

            {!recoveryMode ? (
              <button
                type="button"
                onClick={() => { setRecoveryMode(true); setRecoveryError(''); setRecoverySent(false); }}
                className="w-full text-sm text-indigo-600 hover:text-indigo-800 transition-colors"
              >
                Passwort vergessen?
              </button>
            ) : (
              <div className="space-y-3 border-t border-gray-100 pt-5">
                {recoverySent ? (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-start gap-2">
                    <SafeIcon icon={FiIcons.FiCheckCircle} className="mt-0.5" />
                    <span className="text-sm">
                      Wir haben einen Link zum Zurücksetzen an {recoveryTarget || recoveryEmail} geschickt.
                      Bitte Posteingang (und Spam-Ordner) prüfen.
                    </span>                  </div>
                ) : (
                  <>
                    {recoveryError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                        <SafeIcon icon={FiIcons.FiAlertCircle} />
                        <span className="text-sm">{recoveryError}</span>
                      </div>
                    )}
                    <p className="text-sm text-gray-600">
                      E-Mail-Adresse eingeben – du erhältst einen Link, um ein neues Passwort
                      zu setzen.
                    </p>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <SafeIcon icon={FiIcons.FiMail} className="text-gray-400" />
                      </div>
                      <input
                        id="recovery-email"
                        name="recovery-email"
                        type="text"
                        inputMode="email"
                        autoComplete="email"
                        value={recoveryEmail}
                        onChange={(e) => setRecoveryEmail(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                        placeholder="E-Mail-Adresse oder Benutzername"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleRecovery}
                      disabled={recoveryLoading}
                      className="w-full bg-indigo-50 text-indigo-700 font-semibold py-3 rounded-lg hover:bg-indigo-100 transition-all disabled:opacity-60"
                    >
                      {recoveryLoading ? 'Wird gesendet…' : 'Link anfordern'}
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => { setRecoveryMode(false); setRecoverySent(false); setRecoveryError(''); }}
                  className="w-full text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Zurück zur Anmeldung
                </button>
              </div>
            )}

            <p className="text-xs text-gray-500 text-center leading-relaxed">
              Anmeldung mit Benutzername (z. B. Dervis90) oder E-Mail-Adresse.
              <br />
              Der Zugang erfolgt über die firmeneigene Appwrite-Kontenverwaltung.
              Konten und Passwörter werden dort durch die Administration gepflegt.
            </p>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
